import { useGlobalState, useTrackSessions } from '@/hooks';
import { listEmailSyncAccounts } from '@/modules/pmt/fetchers/emailSync.fetchers';
import type { IEmailAccount, IEmailSyncAccount } from '@/modules/pmt/interfaces/tracking/emailSync.interfaces';
import { mapEmailSyncAccount } from '@/modules/pmt/mappers/emailSyncAccount.mapper';
import { useCallback, useEffect, useState } from 'react';

const MAX_DISCONNECTED_EMAILS_SHOWN = 3;

export type TEmailSyncData = IEmailSyncAccount & {
  hasEmailConnected: boolean;
  connectedEmails: IEmailAccount[];
  disconnectedEmails: IEmailAccount[];
};

export function useEmailSyncAccounts() {
  const { userInfo, isLoggedIn } = useGlobalState();
  const [emailSyncData, setEmailSyncData] = useState<TEmailSyncData | undefined>();
  const { triggerGtmSessions } = useTrackSessions(isLoggedIn);
  const [isFetchingEmailAccounts, setIsFetchingEmailAccounts] = useState(true);

  const fetchEmailSyncAccounts = useCallback(async () => {
    const userUuid = userInfo?.id;
    if (!userUuid) return;
    try {
      const response = await listEmailSyncAccounts();
      const mappedResponse = mapEmailSyncAccount(response?.data);
      const emails = mappedResponse?.emails;
      if (!emails) return;

      const { connectedEmails, disconnectedEmails } = emails.reduce<{
        connectedEmails: IEmailAccount[];
        disconnectedEmails: IEmailAccount[];
      }>(
        (acc, curr) => {
          if (curr.realtimeSyncActivated) acc.connectedEmails.push(curr);
          else if (acc.disconnectedEmails.length < MAX_DISCONNECTED_EMAILS_SHOWN) {
            acc.disconnectedEmails.push(curr);
          }

          return acc;
        },
        { connectedEmails: [], disconnectedEmails: [] },
      );

      setEmailSyncData({
        ...mappedResponse,
        emails: [...connectedEmails, ...disconnectedEmails],
        connectedEmails,
        disconnectedEmails,
        hasEmailConnected: !!connectedEmails.length,
      });
    } catch (e) {
      console.error('List email sync accounts failed', e);
    }
  }, [userInfo?.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: triggerGtmSessions is stable and intentionally omitted
  useEffect(() => {
    triggerGtmSessions(true, {
      emailSyncOptIn: emailSyncData?.hasEmailConnected ? 'Yes' : 'No',
      numberOfEmailSyncAccounts: emailSyncData?.connectedEmails?.length ?? 0,
    });
  }, [emailSyncData]);

  return {
    emailSyncData,
    fetchEmailSyncAccounts,
    isFetchingEmailAccounts,
    setIsFetchingEmailAccounts,
  };
}
